/**
 * Pure pricing / charge math for the checkout flows.
 *
 * Extracted from the Lit checkout components so the money-handling logic —
 * line items, totals, which attendees actually get charged, the Stripe charge
 * payload, coupon placement — can be unit-tested without a DOM. The components
 * are thin wrappers that feed their state into these functions.
 */
import { parsePhoneNumberFromString } from 'libphonenumber-js/min';
import { isPack, type PricingTier } from '../components/data/mock-pricing';
import type { ChargeItem } from '../payments/stripe-client';

/** The minimal attendee shape the math needs (a superset is fine). */
export interface AttendeeLike {
  tierId: string;
  useCredit: boolean;
  seat?: string;
  /**
   * Zipper product id of an existing credit-pack the signed-in member already
   * holds and chose to redeem for this attendee. When set, the attendee is
   * covered by that credit — no charge is built and registration consumes the
   * credit server-side instead of paying.
   */
  memberCreditProductId?: string;
  /**
   * PLAT-6791: product of the member's own All Access pack covering this
   * attendee. Registered with `accessedWithProductId`, never charged.
   */
  memberAccessProductId?: string;
}

/** An attendee paired with its display label. */
export interface LabeledAttendee<A extends AttendeeLike = AttendeeLike> {
  a: A;
  label: string;
}

export interface LineItem {
  label: string;
  sub: string;
  amount: number;
  free: boolean;
  /** Covered by an owned credit-pack (show "Credit used", not "Free"). */
  credit?: boolean;
}

/** Safe fallback when live pay options return no tiers (free / credit-only booking). */
export const DEFAULT_TIER: PricingTier = {
  id: 'dropin',
  label: 'Drop-in',
  sub: 'This class only',
  price: 0,
  perSession: 0,
  sessions: 1,
  recurring: false,
};

/** Resolve a tier by id, falling back to the first tier or {@link DEFAULT_TIER}. */
export function findTier(tiers: PricingTier[], id: string): PricingTier {
  return tiers.find((t) => t.id === id) ?? tiers[0] ?? DEFAULT_TIER;
}

/**
 * A shared family pack is available only when booking for family AND at least
 * one (non-credit) attendee is paying for a multi-use pack whose extra credits
 * can cover the others.
 */
export function familyPackAvailable(
  recipientType: string,
  attendees: LabeledAttendee[],
  tiers: PricingTier[],
): boolean {
  if (recipientType !== 'family') return false;
  return attendees.some(
    ({ a }) => !a.useCredit && isPack(findTier(tiers, a.tierId)),
  );
}

/** One summary line per attendee; credit-covered attendees show as free. */
export function lineItems(
  attendees: LabeledAttendee[],
  tiers: PricingTier[],
  familyPackAvail: boolean,
): LineItem[] {
  return attendees.map(({ a, label }) => {
    const t = findTier(tiers, a.tierId);
    if (a.memberCreditProductId) {
      return { label, sub: 'Your credit pack', amount: 0, free: true, credit: true };
    }
    if (a.memberAccessProductId) {
      return { label, sub: t.label, amount: 0, free: true };
    }
    if (a.useCredit && familyPackAvail && !isPack(t)) {
      return { label, sub: 'Family pack credit', amount: 0, free: true };
    }
    return { label, sub: t.label, amount: t.price, free: false };
  });
}

export function sumLineItems(items: LineItem[]): number {
  return items.reduce((s, li) => s + li.amount, 0);
}

/** Total after a dollar discount, never below zero. */
export function applyDiscount(subtotal: number, discount: number): number {
  return Math.max(0, subtotal - discount);
}

/** Attendees who actually get charged (skips those covered by a credit). */
export function chargedAttendees<A extends AttendeeLike>(
  attendees: LabeledAttendee<A>[],
  tiers: PricingTier[],
  familyPackAvail: boolean,
): LabeledAttendee<A>[] {
  return attendees.filter(({ a }) => {
    if (a.memberCreditProductId) return false;
    // A $0 All Access line is not a sale: charging it would put the member's
    // own membership product on an invoice next to any paying guests.
    if (a.memberAccessProductId) return false;
    const t = findTier(tiers, a.tierId);
    return !(a.useCredit && familyPackAvail && !isPack(t));
  });
}

/** PLAT-6791: attendees covered by their own All Access pack. */
export function accessCoveredAttendees<A extends AttendeeLike>(
  attendees: LabeledAttendee<A>[],
): LabeledAttendee<A>[] {
  return attendees.filter(
    ({ a }) => !!a.memberAccessProductId && !a.memberCreditProductId,
  );
}

/**
 * Attendees covered by an existing member credit-pack. These register by
 * consuming the credit (via `consumeCreditForProductId`) rather than being
 * charged, so they're handled separately from the paid registrations.
 */
export function creditCoveredAttendees<A extends AttendeeLike>(
  attendees: LabeledAttendee<A>[],
): { att: LabeledAttendee<A>; productId: string }[] {
  return attendees
    .filter(({ a }) => !!a.memberCreditProductId)
    .map((att) => ({ att, productId: att.a.memberCreditProductId! }));
}

/** Build the Stripe charge payload, one item per charged attendee. */
export function buildChargeItems(
  charged: LabeledAttendee[],
  tiers: PricingTier[],
  className: string,
): ChargeItem[] {
  return charged.map(({ a, label }) => {
    const t = findTier(tiers, a.tierId);
    const seat = a.seat ? ` · seat ${a.seat}` : '';
    return {
      stripeProductId: t.stripeProductId ?? '',
      ...(t.deferredChargeSettings?.enabled ? { deferredChargeSettings: t.deferredChargeSettings } : {}),
      amount: t.price,
      quantity: 1,
      customDescription: `${className} · ${label} · ${t.label}${seat}`,
    };
  });
}

/** Sum of charge items (amount × quantity), used by the payment modal. */
export function sumItems(
  items: { amount: number; quantity?: number }[],
): number {
  return items.reduce((s, i) => s + i.amount * (i.quantity ?? 1), 0);
}

/**
 * Attach a validated coupon to the first item only; the backend applies it
 * invoice-wide. Returns the list unchanged when there's no coupon.
 */
export function attachCouponToFirst<T extends { coupon?: { id: string } }>(
  items: T[],
  couponId: string,
): T[] {
  if (!couponId) return items;
  return items.map((it, i) =>
    i === 0 ? { ...it, coupon: { id: couponId } } : it,
  );
}

/** Whether a pricing cadence string represents a recurring (monthly) charge. */
export function isRecurringCadence(cadence: string): boolean {
  return /month|mo\b|\/mo/i.test(cadence);
}

/** Strip a phone string to digits only; use `phoneE164Digits` for intl. */
export function phoneDigits(phone?: string): string {
  return (phone ?? '').replace(/\D/g, '');
}

/** Digits after an optional leading `+` (E.164 body, no plus). */
export function phoneE164Digits(phone?: string): string {
  const raw = (phone ?? '').trim();
  if (raw.startsWith('+')) {
    return raw.slice(1).replace(/\D/g, '');
  }
  return phoneDigits(raw);
}

/** Format digits as US `(123) 123-4567` while typing (max 10 digits). */
export function formatUsPhone(raw: string): string {
  const d = phoneDigits(raw).slice(0, 10);
  if (d.length <= 3) return d.length ? `(${d}` : '';
  if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

/**
 * Preserve international and NZ-looking input while typing. Other bare
 * numbers keep the familiar US mask.
 */
export function formatPhoneInput(raw: string): string {
  const trimmed = raw.trim();
  if (trimmed.startsWith('+')) {
    const digits = trimmed.slice(1).replace(/\D/g, '').slice(0, 15);
    return digits.length ? `+${digits}` : '+';
  }
  const digits = phoneDigits(trimmed);
  if (digits.startsWith('0') || digits.startsWith('64')) {
    return digits.slice(0, 15);
  }
  return formatUsPhone(raw);
}

/** True when the visitor entered a complete US phone (10 digits). */
export function isValidUsPhone(phone?: string): boolean {
  return phoneDigits(phone).length === 10;
}

const TRUNK_ZERO_COUNTRIES = ['GB', 'AU', 'IE'] as const;

/**
 * US-format numbers, valid NZ/UK/AU/IE domestic numbers, or explicit
 * international numbers. Bare digits valid in both NZ and US require a
 * country prefix.
 * Callers gate with `!requirePhone || hasValidPhone(phone)`.
 */
export function hasValidPhone(phone?: string): boolean {
  const p = (phone ?? '').trim();
  if (p.startsWith('+')) {
    const n = phoneE164Digits(p);
    if (n.startsWith('1')) return n.length === 11;
    return !!parsePhoneNumberFromString(p)?.isValid();
  }
  const digits = phoneDigits(p);
  if (digits.startsWith('0') || digits.startsWith('64')) {
    const nzPhone = digits.startsWith('0')
      ? parsePhoneNumberFromString(p, 'NZ')
      : parsePhoneNumberFromString(`+${digits}`);
    const validNz = nzPhone?.country === 'NZ' && nzPhone.isValid();
    // PLAT-7387: other trunk-0 countries' national numbers (07946 542497).
    const validOther =
      digits.startsWith('0') &&
      TRUNK_ZERO_COUNTRIES.some((country) => {
        const parsed = parsePhoneNumberFromString(p, country);
        return parsed?.country === country && parsed.isValid();
      });
    const validUs =
      digits.length === 10 &&
      !!parsePhoneNumberFromString(digits, 'US')?.isValid();
    return (validNz || validOther) !== validUs;
  }
  return digits.length === 10;
}

/**
 * C723-05: when phone is optional, reject partial input like `(123` — empty is OK.
 */
export function hasInvalidOptionalPhone(phone?: string): boolean {
  const p = (phone ?? '').trim();
  if (!p) return false;
  return !hasValidPhone(p);
}

/** Basic email shape check for checkout step gating. */
export function isValidEmail(email?: string): boolean {
  const e = (email ?? '').trim();
  return !!e && /\S+@\S+\.\S+/.test(e);
}
